import { poissonHistogramData } from "@/lib/poisson";

/**
 * Mini SVG histogram of a Poisson distribution.
 * Display-only: visualises the distribution with G and G_crit markers.
 * Computations are purely presentational — the verdict comes from the
 * snapshot. Ported from
 * products/x2mail-admin/src/views/_components/PoissonHistogram.tsx.
 */
export interface PoissonHistogramProps {
  mu: number;
  gObs: number; // observed G (follower count)
  gCrit: number; // G_crit from snapshot (-1 = no threshold)
}

const WIDTH = 320;
const HEIGHT = 120;
const BAR_GAP = 1;

export function PoissonHistogram({ mu, gObs, gCrit }: PoissonHistogramProps) {
  if (mu <= 0) {
    return <p className="text-xs text-zinc-500">μ = 0 — brak danych do wykresu rozkładu.</p>;
  }

  const data = poissonHistogramData(mu, gObs, gCrit);
  const maxPmf = Math.max(...data.map((d) => d.pmf), 0.001);
  const barW = Math.max(2, (WIDTH - BAR_GAP * (data.length - 1)) / data.length);

  return (
    <div>
      <p className="mb-1 text-xs text-zinc-500">
        Rozkład Poisson(μ={mu.toFixed(2)}) — oś Y: prawdopodobieństwo
      </p>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width={WIDTH}
        height={HEIGHT}
        role="img"
        aria-label={`Histogram Poissona μ=${mu.toFixed(2)}, obserwowane G=${gObs}, próg G_crit=${gCrit}`}
      >
        {data.map((d, i) => {
          const barH = (d.pmf / maxPmf) * (HEIGHT - 20);
          const x = i * (barW + BAR_GAP);
          const y = HEIGHT - 20 - barH;

          let fill = "#3f3f46"; // zinc-700 default
          if (d.isObs)
            fill = "#22c55e"; // green-500: observed G
          else if (d.isCritOrBelow) fill = "#ef4444"; // red-500: alert zone

          return (
            <g key={d.k}>
              <rect x={x} y={y} width={barW} height={barH} fill={fill} rx={1} />
              {/* Show k label for key values only (avoid clutter) */}
              {(d.k % Math.max(1, Math.round(data.length / 8)) === 0 ||
                d.isObs ||
                (gCrit >= 0 && d.k === gCrit)) && (
                <text
                  x={x + barW / 2}
                  y={HEIGHT - 4}
                  textAnchor="middle"
                  fontSize={9}
                  fill="#71717a"
                >
                  {d.k}
                </text>
              )}
            </g>
          );
        })}
        {/* Legend */}
        <rect x={0} y={0} width={10} height={8} fill="#ef4444" rx={1} />
        <text x={14} y={8} fontSize={8} fill="#71717a">
          strefa alertu (≤G_crit)
        </text>
        <rect x={100} y={0} width={10} height={8} fill="#22c55e" rx={1} />
        <text x={114} y={8} fontSize={8} fill="#71717a">
          obserwowane G={gObs}
        </text>
      </svg>
    </div>
  );
}
